<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import {
  iButton,
  iModal,
  iSpinner,
  iTextInput,
  openModal,
} from '@inscribe/signal';
import RuleEngine from '@/utils/ruleEngine';
import CheckDelete from '@/V2/decision-engine/checks/CheckDelete.vue';
import DecisionEngineCard from '@/V2/decision-engine/DecisionEngineCard.vue';
import EmptyPage from '@/V2/components/EmptyPage';

export default {
  components: {
    CheckDelete,
    DecisionEngineCard,
    EmptyPage,
    iButton,
    iModal,
    iSpinner,
    iTextInput,
  },
  data() {
    return {
      showNewRuleModal: false,
      newRule: null,
      ruleError: null,
      mode: '',
      displayLink: 'https://help.inscribe.ai/en/articles/5677805-checks',
      editingCheck: null,
      checkToBeDeleted: null,
      newCheckName: '',
    };
  },
  computed: {
    ...mapGetters({
      isOrgAdmin: 'isOrgAdmin',
    }),
    checks() {
      return this.$store.state.config.checks.slice().reverse();
    },
    validCheckName() {
      return this.newCheckName.trim() !== '';
    },
    checksLoading() {
      return this.$store.state.config.checksLoading;
    },
    isValidRuleSet() {
      return (
        this.rulesSchema &&
        this.rulesSchema.variables &&
        new RuleEngine(this.rulesSchema).isValidRuleSet(
          this.$store.state.config.rules,
        )
      );
    },
  },
  mounted() {
    if (this.isOrgAdmin) {
      this.getConfigChecks();
    }
  },
  methods: {
    ...mapActions({
      getConfigChecks: 'getConfigChecks',
      deleteCheck: 'deleteCheck',
      saveCheck: 'saveCheck',
    }),
    ...mapGetters({ getChecks: 'getChecks' }),
    ...mapMutations({
      setChecks: 'setChecks',
    }),
    getCheckSubtitle(check) {
      return `${
        check.triggers.length > 0
          ? check.triggers.length === 1
            ? '1 document filter'
            : `${check.triggers.length} document filters`
          : 'All documents'
      } • ${
        check.requirements.length > 0
          ? check.requirements.length === 1
            ? '1 requirement'
            : `${check.requirements.length} requirements`
          : 'No requirements'
      } • ${check.public ? 'Show to users' : 'Hide from users'}`;
    },
    onDeleteClick(data) {
      this.checkToBeDeleted = data.item;
    },
    addNewCheck() {
      openModal('new-checks-modal');
    },
    onCheckNameChange(value) {
      this.newCheckName = value;
    },
    createCheck() {
      const newCheck = {
        name: this.newCheckName.trim(),
        isDraft: true,
        enabled: true,
        public: false,
        requirements: [
          {
            name: null,
            operator: null,
            value: null,
          },
        ],
        triggers: [],
      };

      this.setChecks([...this.getChecks(), newCheck]);

      this.$router.push({
        name: 'decision-engine-checks-edit',
        params: { check: newCheck },
      });
    },

    onEditClick(params) {
      this.$emit('hide-tooltips');
      this.$router.push({
        name: 'decision-engine-checks-edit',
        params: { check: params.item },
      });
    },
  },
};
</script>

<template>
  <div class="checks-page">
    <div class="content d-flex flex-column i-gap-12">
      <div class="d-flex justify-content-between align-items-center">
        <div class="text flex-grow-1">
          <div class="i-text-heading-5 i-text-neutral-120 i-mb-2">
            Document Checks
          </div>
          <div class="i-text-body-2 i-text-neutral-110 subheading-wrap">
            Checks are sets of requirements that can automatically be applied to
            your documents.
            <a rel="noopener" class="i-link" :href="displayLink" target="_blank"
              >Learn more</a
            >
          </div>
        </div>
        <div v-if="checks.length > 0" class="flex-shrink-0">
          <i-button
            v-if="isOrgAdmin"
            text="New Check"
            data-testid="new-check-button"
            size="md"
            @button-click="addNewCheck()"
          ></i-button>
        </div>
      </div>

      <!-- empty page -->
      <empty-page
        v-if="checks.length === 0 && !checksLoading"
        class="d-flex flex-grow-1"
        icon="large-file-search"
        title="Start by creating a new document check"
        main-button-data-test-id="new-check-button"
        subtitle="All of your document checks will appear here."
        :button-text="isOrgAdmin ? 'New Check' : null"
        @on-button-click="addNewCheck"
      ></empty-page>

      <!-- loading screen-->
      <div
        v-if="checksLoading"
        class="d-flex my-5 w-100 align-items-center justify-content-center"
      >
        <i-spinner></i-spinner>
      </div>

      <!-- list -->
      <div
        v-if="checks.length > 0 && !checksLoading"
        class="checks-list d-flex flex-column align-items-start i-radius-2xl"
      >
        <decision-engine-card
          v-for="(check, checkIndex) in checks"
          :key="checkIndex"
          :title="check.name"
          :subtitle="getCheckSubtitle(check)"
          :item="check"
          tooltip-toggle-on="Disable Check"
          tooltip-toggle-off="Enable Check"
          tooltip-edit="Edit Check"
          tooltip-delete="Delete Check"
          data-testid="check-card"
          @delete-click="onDeleteClick"
          @edit-click="onEditClick"
          @card-click="onEditClick"
        ></decision-engine-card>
      </div>
    </div>

    <!-- new Check Modal -->
    <i-modal
      modal-id="new-checks-modal"
      data-testid="new-checks-modal"
      title="New Document Check"
      :confirm-button-disabled="!validCheckName"
      confirm-button-text="Continue"
      :submit-on-enter="true"
      :input-focus-on-open="true"
      @on-submit="createCheck"
    >
      <div slot="body-content">
        <i-text-input
          ref="newCheckInput"
          :value="newCheckName"
          placeholder="Check name"
          :show-hint-text="true"
          hint-text="For example: ‘Bank Statement Validity’ or ‘KYB Requirements’"
          data-testid="new-checks-modal-input"
          @input="onCheckNameChange"
        ></i-text-input>
      </div>
    </i-modal>

    <!-- check delete -->
    <check-delete
      :check-to-be-deleted="checkToBeDeleted"
      @on-close="checkToBeDeleted = null"
    ></check-delete>
  </div>
</template>

<style lang="scss" scoped>
.checks-page {
  height: 100%;

  .content {
    height: 100%;
  }

  .checks-list {
    $border-radius: 16px;

    border: 1px solid $light-neutral-60;

    .decision-engine-card {
      border-bottom: 1px solid $light-neutral-60;
      width: 100%;
    }

    .decision-engine-card:last-of-type {
      border-bottom: 0;
      border-bottom-left-radius: $border-radius;
      border-bottom-right-radius: $border-radius;
    }

    .decision-engine-card:first-of-type {
      border-top-left-radius: $border-radius;
      border-top-right-radius: $border-radius;
    }
  }
}

.subheading-wrap {
  max-width: 70%;
}
</style>
